const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/kisapi/appointments.ts';
import { compileEndpoint } from './utils';

const APPOINTMENT_FIELDS_EXCLUDING_CONTENT = [
    'BEKAT',
    'COMM',
    'DAT',
    'EXPTYP',
    'FALLNR',
    'FCODE',
    'FKEY',
    'GROUPID',
    'ISOLATION',
    'KAT',
    'KIND',
    'KLASSE',
    'KLINIK',
    'LAST_MODIFIED',
    'MUTDAT',
    'MUTVIS',
    'PATNAME',
    'PATNR',
    'PROC1',
    'PROC2',
    'REF_NR',
    'REF_SENDER',
    'RESNAME',
    'SENDER',
    'SEX',
    'STARTDAT',
    'STAT',
    'STAT1',
    'STAT2',
    'STOPDAT',
    'TERMNR',
    'TERMTXT',
    'TYP',
    'VISUM',
].join(',');

/**
 * Loads a single appointment (KisTermin)
 */
export function apiLoad1Appointment(
    termnr: string | null,
    disallowArrays: boolean = true,
    resolveRefs: boolean = false,
    withContent: boolean = true
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    let fields = APPOINTMENT_FIELDS_EXCLUDING_CONTENT;
    if (withContent) fields += ',CONTENT';
    if (resolveRefs) fields += ',OrigRec';

    return {
        endpoint: compileEndpoint(
            ['CalendarItems', termnr],
            [
                !resolveRefs ? null : { key: 'IncludeOrigRec', value: '1' }, //
                { key: 'Fields', value: fields },
            ]
        ),
        requestConfig,
    };
}

/**
 * Loads the badge information for a specific appointment.
 * @param termnr The appointment whose badge is to be loaded
 * @param badgeKind Either 'STATUS' or 'KIND'
 * @param disallowArrays true=prevent any attempts of converting Qstructs to JS arrays
 * @param resolveRefs true=resolve Referenztermine
 */
export function apiLoadDispoBadge(
    termnr: string,
    badgeKind: string,
    disallowArrays: boolean = true,
    resolveRefs: boolean = true
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['CalendarItems', termnr, 'Badge', badgeKind],
            [!resolveRefs ? null : { key: 'ResolveRefs', value: '1' }]
        ),
        requestConfig,
    };
}

/**
 * Loads a list of appointments involving a given patient.
 * @param patnr
 * @param fallnr
 * @param mindat
 * @param maxdat
 * @param stat
 * @param withContent specifies how the CONTENT should be loaded:
 *   true=load the full content
 *   false=don't load the CONTENT
 *   string[]=return only the given Qscript-paths of the CONTENT (path elements delimited by slash)
 * @param disallowArrays
 * @param resolveRefs
 * @param transformRtf
 * @param type filters by TYP
 * @param kat filters by KAT
 * @param proc1 filters by PROC1
 * @param fields array of fields wanted in response; undefined = all fields (except CONTENT unless withContent is true)
 */
export function apiLoadAppointmentsOfPat(
    patnr: string,
    fallnr: string | undefined,
    mindat: string | undefined,
    maxdat: string | undefined,
    stat: string | undefined,
    withContent: boolean | string[],
    disallowArrays: boolean = false,
    resolveRefs: boolean = false,
    transformRtf: boolean = false,
    type?: string,
    kat?: string,
    proc1?: string,
    fields?: string[]
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    const contentPaths = Array.isArray(withContent) && withContent.length > 0 && withContent.join(',');

    return {
        endpoint: compileEndpoint(
            ['CalendarItems'],
            [
                { key: 'PATNR', value: patnr },
                fallnr && { key: 'FallFilter', value: fallnr },
                mindat && { key: 'MINDAT', value: mindat },
                maxdat && { key: 'MAXDAT', value: maxdat },
                stat && { key: 'STAT', value: stat },
                type && { key: 'TYP', value: type },
                kat && { key: 'KAT', value: kat },
                proc1 && { key: 'PROC1', value: proc1 },
                { key: 'Fields', value: fields ? fields.join(',') : withContent === true ? 'ALLCONTENT' : 'ALL' },
                contentPaths && { key: 'ContentPaths', value: contentPaths },
                resolveRefs && { key: 'IncludeOrigRec', value: '1' },
                transformRtf && { key: 'Trf', value: 'RtfToAnsi' },
            ]
        ),
        requestConfig,
    };
}

/**
 * Loads a list of appointments belonging to a given patient (structured for component Appointment.tsx)
 */
export function apiLoadStructuredAppointmentsOfPat(
    patnr: string,
    mindat: string,
    maxdat: string,
    disallowArrays: boolean = false
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'StructuredCalendarItems'],
            [
                { key: 'PATNR', value: patnr },
                mindat && { key: 'MINDAT', value: mindat },
                maxdat && { key: 'MAXDAT', value: maxdat },
            ]
        ),
        requestConfig,
    };
}

/**
 * Loads a list of appointments belonging to a given resource (structured for component Appointment.tsx)
 */
export function apiLoadStructuredAppointmentsForResource(
    kat: string,
    fkey: string,
    mindat: string,
    maxdat: string,
    disallowArrays: boolean = false
) {
    let requestConfig: any;

    if (disallowArrays) {
        requestConfig = {
            headers: { Accept: 'array=no' },
        };
    }

    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'StructuredCalendarItems'],
            [
                { key: 'KAT', value: kat },
                { key: 'FKEY', value: fkey },
                mindat && { key: 'MINDAT', value: mindat },
                maxdat && { key: 'MAXDAT', value: maxdat },
            ]
        ),
        requestConfig,
    };
}

/**
 * Loads the object-specific rights of a given order.
 */
export function apiLoadCalendarItemsRights(termnr: string) {
    return {
        endpoint: compileEndpoint(
            ['CalendarItems', termnr || '?', 'Rights'], //
            null
        ),
    };
}

/**
 * Loads the list of status values available for a given appointment.
 */
export function apiLoadAppointmentStatus(termnr: string) {
    return {
        endpoint: compileEndpoint(
            ['KisMobile', 'Termin', 'StatusList'], //
            [{ key: 'TERMNR', value: termnr }]
        ),
        requestConfig: {
            headers: { Accept: 'array=no' },
        },
    };
}
